import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { MINERAL_GALLERY, EXTRA_GALLERY } from '../config/images';
import { cn } from '../lib/utils';
import { X, ZoomIn, ChevronLeft, ChevronRight, Sparkles, ArrowLeftRight, Package } from 'lucide-react';

// Mineral color metadata
const mineralInfo = {
  betonite: { 
    code: 'MM-N001', 
    he: 'בטונייט', 
    en: 'Betonite',
    descHe: 'גוון בטון אדריכלי בהיר',
    descEn: 'Light architectural concrete tone',
    hex: '#C5C5C0'
  },
  betonda: { 
    code: 'MM-N002', 
    he: 'בטונדה', 
    en: 'Betonda',
    descHe: 'גוון בטון כהה עם מרקם מינרלי',
    descEn: 'Dark concrete with mineral texture',
    hex: '#6B6B6B'
  },
  mika: { 
    code: 'MM-N003', 
    he: 'מיקה', 
    en: 'Mika',
    descHe: 'גוון בז\' מינרלי טבעי',
    descEn: 'Natural beige mineral tone',
    hex: '#D4C4A8'
  },
  titanium: { 
    code: 'MM-N004', 
    he: 'טיטניום', 
    en: 'Titanium',
    descHe: 'גוון לבן מינרלי טבעי',
    descEn: 'Natural white mineral tone',
    hex: '#F5F5F0'
  },
  clay: { 
    code: 'MM-N005', 
    he: 'חרסית ממשית', 
    en: 'Clay (Terracotta)',
    descHe: 'גוון חימר טרקוטה טבעי',
    descEn: 'Natural terracotta clay tone',
    hex: '#C67B5C'
  },
  yatir: { 
    code: 'MM-N006', 
    he: 'יתיר', 
    en: 'Yatir',
    descHe: 'גוון צהוב מינרלי טבעי',
    descEn: 'Natural yellow mineral tone',
    hex: '#D4A060'
  },
};

// Quick View Modal (Short tap)
const QuickViewModal = ({ isOpen, onClose, image, info, isRTL, isExtra = false }) => {
  if (!isOpen) return null;

  // Handle different property names for mineral vs extra items
  const title = isExtra 
    ? (isRTL ? info?.titleHe : info?.titleEn)
    : (isRTL ? info?.he : info?.en);
  const description = isExtra
    ? (isRTL ? info?.descriptionHe : info?.descriptionEn)
    : (isRTL ? info?.descHe : info?.descEn);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop with blur */}
      <motion.div 
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />
      
      {/* Card */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 50 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative max-w-md w-full bg-card rounded-3xl overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 end-4 z-10 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/50 transition-colors"
          data-testid="quick-view-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Image */}
        <div className="aspect-square relative overflow-hidden">
          <motion.img
            src={image}
            alt={title}
            className="w-full h-full object-cover"
            layoutId={`gallery-image-${info?.code || 'extra'}`}
          />
          
          {/* Color indicator */}
          {info?.hex && (
            <div 
              className="absolute bottom-4 start-4 w-12 h-12 rounded-xl shadow-lg border-2 border-white/50"
              style={{ backgroundColor: info.hex }}
              data-testid="quick-view-color-indicator"
            />
          )}
        </div>

        {/* Info */}
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              {info?.code && (
                <span className="text-sm font-mono text-accent" data-testid="quick-view-color-code">{info.code}</span>
              )}
              <h3 className="text-2xl font-bold text-foreground mt-1" data-testid="quick-view-title">
                {title}
              </h3>
              <p className="text-muted-foreground mt-2" data-testid="quick-view-description">
                {description}
              </p>
            </div>
            <Sparkles className="w-6 h-6 text-accent flex-shrink-0" />
          </div>
          
          {info?.code && (
            <p className="text-xs text-muted-foreground/70 mt-4 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-accent" />
              {isRTL 
                ? 'ציפוי מינרלי טבעי – ללא פיגמנטים מלאכותיים'
                : 'Natural mineral coating – no synthetic pigments'
              }
            </p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Full Screen Lightbox (Long press)
const FullScreenLightbox = ({ isOpen, onClose, images, currentIndex, setCurrentIndex, isRTL }) => {
  if (!isOpen) return null;
  
  const allKeys = [...Object.keys(images.minerals), ...Object.keys(images.extra)];
  const currentKey = allKeys[currentIndex];
  const isMineral = currentIndex < Object.keys(images.minerals).length;
  const info = isMineral ? mineralInfo[currentKey] : images.extra[currentKey];

  const goNext = () => setCurrentIndex((prev) => (prev + 1) % allKeys.length);
  const goPrev = () => setCurrentIndex((prev) => (prev - 1 + allKeys.length) % allKeys.length);

  const currentImage = isMineral 
    ? images.minerals[currentKey] 
    : images.extra[currentKey]?.url;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white">
        <div className="flex items-center gap-3">
          {isMineral && info?.hex && (
            <div 
              className="w-8 h-8 rounded-lg"
              style={{ backgroundColor: info.hex }}
            />
          )}
          <div>
            {info?.code && (
              <span className="text-white/60 text-sm font-mono">{info.code}</span>
            )}
            <h3 className="text-lg font-bold">
              {isMineral 
                ? (isRTL ? info?.he : info?.en)
                : (isRTL ? info?.titleHe : info?.titleEn)
              }
            </h3>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Main Image with navigation */}
      <div className="flex-1 flex items-center justify-center p-4 relative">
        <button
          onClick={goPrev}
          className="absolute start-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors text-white z-10"
        >
          {isRTL ? <ChevronRight className="w-6 h-6" /> : <ChevronLeft className="w-6 h-6" />}
        </button>
        
        <AnimatePresence mode="wait">
          <motion.img
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            src={currentImage}
            alt=""
            className="max-h-[65vh] max-w-full object-contain rounded-2xl shadow-2xl"
          />
        </AnimatePresence>
        
        <button
          onClick={goNext}
          className="absolute end-2 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors text-white z-10"
        >
          {isRTL ? <ChevronLeft className="w-6 h-6" /> : <ChevronRight className="w-6 h-6" />}
        </button>
      </div>

      {/* Description */}
      <div className="p-4 text-center text-white/80">
        <p className="text-sm">
          {isMineral 
            ? (isRTL ? info?.descHe : info?.descEn)
            : (isRTL ? info?.descriptionHe : info?.descriptionEn)
          }
        </p>
      </div>

      {/* Thumbnail strip */}
      <div className="flex justify-center gap-2 p-4 overflow-x-auto pb-6">
        {Object.keys(images.minerals).map((key, idx) => (
          <button
            key={key}
            onClick={() => setCurrentIndex(idx)}
            className={cn(
              'w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all',
              idx === currentIndex 
                ? 'border-white scale-110' 
                : 'border-transparent opacity-50 hover:opacity-80'
            )}
          >
            <img src={images.minerals[key]} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
        <div className="w-px bg-white/20 mx-2" />
        {Object.keys(images.extra).map((key, idx) => (
          <button
            key={key}
            onClick={() => setCurrentIndex(Object.keys(images.minerals).length + idx)}
            className={cn(
              'w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all',
              (Object.keys(images.minerals).length + idx) === currentIndex 
                ? 'border-white scale-110' 
                : 'border-transparent opacity-50 hover:opacity-80'
            )}
          >
            <img src={images.extra[key].url} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
    </motion.div>
  );
};

// Gallery Card with tap/long-press
const GalleryCard = ({ imageKey, image, info, index, onTap, onLongPress, isRTL, isExtra = false }) => {
  const longPressTimer = useRef(null);
  const [isPressed, setIsPressed] = useState(false);

  const handlePointerDown = () => {
    setIsPressed(true);
    longPressTimer.current = setTimeout(() => {
      onLongPress();
      setIsPressed(false);
    }, 500);
  };

  const handlePointerUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      if (isPressed) {
        onTap();
      }
    }
    setIsPressed(false);
  };

  const handlePointerLeave = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
    setIsPressed(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: index * 0.08, duration: 0.5 }}
      className="relative group"
    >
      <motion.button
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        whileTap={{ scale: 0.95 }}
        className={cn(
          'relative w-full aspect-square rounded-2xl overflow-hidden',
          'bg-gradient-to-br from-secondary/50 to-secondary',
          'border border-border/30 hover:border-accent/50',
          'shadow-lg hover:shadow-xl hover:shadow-accent/10',
          'transition-all duration-300',
          isPressed && 'scale-95 shadow-inner'
        )}
      >
        {/* Image */}
        <img
          src={isExtra ? info?.url : image}
          alt={isRTL ? (isExtra ? info?.titleHe : info?.he) : (isExtra ? info?.titleEn : info?.en)}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          draggable={false}
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Color indicator for minerals */}
        {!isExtra && info?.hex && (
          <motion.div 
            className="absolute top-3 start-3 w-6 h-6 rounded-lg border-2 border-white/50 shadow-lg"
            style={{ backgroundColor: info.hex }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: index * 0.1 + 0.3 }}
          />
        )}

        {/* Type badge for extra items */}
        {isExtra && (
          <div className="absolute top-3 start-3">
            {info?.type === 'comparison' && (
              <div className="w-8 h-8 rounded-lg bg-accent/90 flex items-center justify-center">
                <ArrowLeftRight className="w-4 h-4 text-white" />
              </div>
            )}
            {info?.type === 'product' && (
              <div className="w-8 h-8 rounded-lg bg-secondary/90 flex items-center justify-center">
                <Package className="w-4 h-4 text-foreground" />
              </div>
            )}
          </div>
        )}

        {/* Hover info */}
        <div className="absolute bottom-0 inset-x-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          {!isExtra && info?.code && (
            <span className="text-white/70 text-xs font-mono">{info.code}</span>
          )}
          <p className="text-white font-semibold text-sm mt-0.5 line-clamp-1">
            {isRTL 
              ? (isExtra ? info?.titleHe : info?.he) 
              : (isExtra ? info?.titleEn : info?.en)
            }
          </p>
        </div>

        {/* Long press indicator */}
        <AnimatePresence>
          {isPressed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex items-center justify-center bg-black/40"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                className="w-16 h-16 rounded-full border-4 border-white/50 flex items-center justify-center"
              >
                <ZoomIn className="w-6 h-6 text-white" />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </motion.div>
  );
};

export const ProductGallery = () => {
  const { isRTL } = useLanguage();
  const [quickViewData, setQuickViewData] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const mineralKeys = Object.keys(MINERAL_GALLERY);
  const extraKeys = Object.keys(EXTRA_GALLERY);

  const handleTap = (key, isExtra = false) => {
    if (isExtra) {
      setQuickViewData({
        image: EXTRA_GALLERY[key].url,
        info: EXTRA_GALLERY[key],
        isExtra: true,
      });
    } else {
      setQuickViewData({
        image: MINERAL_GALLERY[key],
        info: mineralInfo[key],
        isExtra: false,
      });
    }
  };

  const handleLongPress = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <section id="gallery-section" className="py-10 md:py-14 px-4 bg-gradient-to-b from-background to-secondary/30">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 text-accent text-sm font-medium mb-4"
          >
            <Sparkles className="w-4 h-4" />
            {isRTL ? 'ציפויים מינרליים טבעיים' : 'Natural Mineral Coatings'}
          </motion.div>
          
          <h2 className="text-3xl sm:text-4xl font-display text-foreground mb-3">
            {isRTL ? 'גלריית יצירות' : 'Creation Gallery'}
          </h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            {isRTL 
              ? 'לחיצה קצרה לתצוגה מהירה • לחיצה ארוכה להגדלה'
              : 'Short tap for quick view • Long press to enlarge'
            }
          </p>
        </motion.div>

        {/* Main Gallery Grid - Minerals */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">
          {mineralKeys.map((key, index) => (
            <GalleryCard
              key={key}
              imageKey={key}
              image={MINERAL_GALLERY[key]}
              info={mineralInfo[key]}
              index={index}
              onTap={() => handleTap(key)}
              onLongPress={() => handleLongPress(index)}
              isRTL={isRTL}
            />
          ))}
        </div>

        {/* Separator */}
        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-border" />
          <span className="text-sm text-muted-foreground">
            {isRTL ? 'עוד מהגלריה' : 'More from Gallery'}
          </span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Extra Gallery Items */}
        <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
          {extraKeys.map((key, index) => (
            <GalleryCard
              key={key}
              imageKey={key}
              image={EXTRA_GALLERY[key].url}
              info={EXTRA_GALLERY[key]}
              index={mineralKeys.length + index}
              onTap={() => handleTap(key, true)}
              onLongPress={() => handleLongPress(mineralKeys.length + index)}
              isRTL={isRTL}
              isExtra
            />
          ))}
        </div>

        {/* Footer branding */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-8"
        >
          <p className="text-xs text-muted-foreground">
            M&apos;art Microlit • {isRTL ? 'ציפויים מינרליים מקצועיים מאז 1958' : 'Professional Mineral Coatings Since 1958'}
          </p>
        </motion.div>
      </div>

      {/* Quick View Modal */}
      <AnimatePresence>
        {quickViewData && (
          <QuickViewModal
            isOpen={!!quickViewData}
            onClose={() => setQuickViewData(null)}
            image={quickViewData.image}
            info={quickViewData.info}
            isRTL={isRTL}
            isExtra={quickViewData.isExtra}
          />
        )}
      </AnimatePresence>

      {/* Full Screen Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <FullScreenLightbox
            isOpen={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
            images={{ minerals: MINERAL_GALLERY, extra: EXTRA_GALLERY }}
            currentIndex={lightboxIndex}
            setCurrentIndex={setLightboxIndex}
            isRTL={isRTL}
          />
        )}
      </AnimatePresence>
    </section>
  );
};
